<div *ngIf="isOpen" class="fixed inset-0 z-[99999] flex items-center justify-center">

  <!-- Backdrop -->
  <div class="absolute inset-0 bg-black/80 backdrop-blur-sm" (click)="onCancel()"></div>

  <!-- Camera Modal -->
  <div class="relative z-10 w-full max-w-lg mx-4 rounded-3xl overflow-hidden shadow-2xl bg-gray-950 flex flex-col"
    style="max-height: 90vh;">

    <!-- ── Header ──────────────────────────────────────────────── -->
    <div class="flex items-center justify-between px-5 py-4 bg-gray-950 border-b border-white/10 flex-shrink-0">
      <div class="flex items-center gap-3">
        <!-- Live recording dot -->
        <span class="relative flex h-3 w-3" *ngIf="streamActive && !capturedImage">
          <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D31148] opacity-75"></span>
          <span class="relative inline-flex rounded-full h-3 w-3 bg-[#D31148]"></span>
        </span>
        <h3 class="text-white font-semibold text-base tracking-tight">
          {{ capturedImage ? 'Preview Capture' : (isFrontCamera ? '📱 Front Camera' : '📷 Back Camera') }}
        </h3>
      </div>
      <button (click)="onCancel()"
        class="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <!-- ── Video / Preview area ────────────────────────────────── -->
    <div class="relative bg-black flex-shrink-0 overflow-hidden" style="aspect-ratio: 4/3;">

      <!--
        MIRROR FIX:
        Front camera live preview uses CSS scaleX(-1) so the user sees
        a natural mirror image (raising RIGHT hand = appears on RIGHT).
        The underlying video pixel data is never flipped — only the
        display is. The canvas capture draws the raw pixels as-is,
        so the saved photo shows the real-world orientation.
      -->
      <video #videoEl
        class="w-full h-full object-cover transition-all duration-300"
        [class.opacity-0]="!!capturedImage"
        [style.transform]="isFrontCamera && !capturedImage ? 'scaleX(-1)' : 'none'"
        autoplay
        playsinline
        muted>
      </video>

      <!-- Captured image preview (not mirrored — real-world orientation) -->
      <img *ngIf="capturedImage"
        [src]="capturedImage"
        class="absolute inset-0 w-full h-full object-cover" />

      <!-- Hidden canvas -->
      <canvas #canvasEl class="hidden"></canvas>

      <!-- Error overlay -->
      <div *ngIf="cameraError"
        class="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gray-950 px-6 text-center">
        <div class="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" class="text-red-400">
            <path d="M23 7l-7 5 7 5V7z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            <rect x="1" y="5" width="15" height="14" rx="2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div>
          <p class="text-white font-semibold text-sm mb-1">Camera Access Issue</p>
          <p class="text-gray-400 text-xs leading-relaxed">{{ cameraError }}</p>
        </div>
        <button (click)="requestCamera()"
          class="mt-1 px-5 py-2 rounded-full bg-[#D31148] text-white text-sm font-semibold hover:bg-[#b80e3d] transition-colors">
          Try Again
        </button>
      </div>

      <!-- Loading overlay -->
      <div *ngIf="!streamActive && !cameraError && !capturedImage"
        class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-950">
        <div class="w-10 h-10 rounded-full border-2 border-white/20 border-t-[#D31148] animate-spin"></div>
        <p class="text-gray-400 text-xs">
          {{ isSwitching ? 'Switching camera…' : 'Starting camera…' }}
        </p>
      </div>

      <!-- Shutter flash overlay -->
      <div #flashEl class="absolute inset-0 bg-white pointer-events-none opacity-0"></div>

      <!-- Zoom indicator (top-left, live only) -->
      <div *ngIf="streamActive && !capturedImage && currentZoom > 1"
        class="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs font-bold backdrop-blur-sm">
        {{ currentZoom.toFixed(1) }}×
      </div>

      <!-- Front/Back badge (top-right, live only) -->
      <div *ngIf="streamActive && !capturedImage"
        class="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm">
        <span class="text-white text-[10px] font-semibold tracking-wide uppercase">
          {{ isFrontCamera ? 'Front' : 'Back' }}
        </span>
      </div>
    </div>

    <!-- ── Controls ─────────────────────────────────────────────── -->
    <div class="flex-shrink-0 bg-gray-950 px-5 py-5">

      <!-- LIVE state -->
      <ng-container *ngIf="!capturedImage">

        <!-- Main control row: Flash · Shutter · Switch · Zoom -->
        <div class="flex items-center justify-between gap-2">

          <!-- Flash -->
          <button (click)="toggleFlash()"
            [disabled]="!streamActive"
            class="flex flex-col items-center gap-1 disabled:opacity-30 transition-opacity flex-shrink-0"
            title="Toggle flash">
            <div class="w-11 h-11 rounded-full flex items-center justify-center transition-colors"
              [class.bg-yellow-400]="flashMode === 'on'"
              [class.bg-white/10]="flashMode !== 'on'">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                [class.text-gray-900]="flashMode === 'on'"
                [class.text-white]="flashMode !== 'on'">
                <path d="M13 2L4.5 13.5H11L10 22L20.5 9.5H14L13 2Z"
                  [attr.fill]="flashMode === 'on' ? 'currentColor' : 'none'"
                  stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <span class="text-[10px] font-medium leading-tight"
              [class.text-yellow-400]="flashMode === 'on'"
              [class.text-gray-400]="flashMode !== 'on'">
              {{ flashMode === 'on' ? 'Flash On' : 'Flash' }}
            </span>
          </button>

          <!-- Shutter (centre, largest) -->
          <button (click)="capturePhoto()"
            [disabled]="!streamActive"
            class="relative flex items-center justify-center rounded-full disabled:opacity-30 active:scale-95 transition-transform focus:outline-none flex-shrink-0"
            style="width: 72px; height: 72px;"
            title="Take photo">
            <span class="absolute inset-0 rounded-full border-4 border-white/60"></span>
            <span class="rounded-full bg-white hover:bg-gray-100 transition-colors shadow-lg"
              style="width: 56px; height: 56px;"></span>
          </button>

          <!-- Switch camera button
               ═══════════════════════════════════════════════════════
               Always visible when stream is active, regardless of how
               many cameras were enumerated. On mobile, the browser
               often returns only 1 device until the user switches —
               so we ALWAYS show this button and let the browser pick
               the best camera for the new facing mode.
               ═══════════════════════════════════════════════════════ -->
          <button (click)="switchCamera()"
            [disabled]="!streamActive || isSwitching"
            class="flex flex-col items-center gap-1 disabled:opacity-30 transition-opacity flex-shrink-0"
            title="{{ isFrontCamera ? 'Switch to back camera' : 'Switch to front camera' }}">
            <div class="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors relative overflow-hidden">
              <!-- Spin while switching -->
              <svg *ngIf="!isSwitching" width="20" height="20" viewBox="0 0 24 24" fill="none" class="text-white">
                <path d="M20 7H9a3 3 0 0 0-3 3v.5M4 17h11a3 3 0 0 0 3-3v-.5"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M17 4l3 3-3 3M7 20l-3-3 3-3"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <div *ngIf="isSwitching"
                class="w-5 h-5 rounded-full border-2 border-white/20 border-t-white animate-spin">
              </div>
            </div>
            <span class="text-[10px] text-gray-400 font-medium leading-tight">
              {{ isFrontCamera ? 'Back' : 'Front' }}
            </span>
          </button>

          <!-- Zoom -->
          <button (click)="cycleZoom()"
            [disabled]="!streamActive"
            class="flex flex-col items-center gap-1 disabled:opacity-30 transition-opacity flex-shrink-0">
            <div class="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
              <span class="text-white text-xs font-bold">{{ currentZoom.toFixed(1) }}×</span>
            </div>
            <span class="text-[10px] text-gray-400 font-medium leading-tight">Zoom</span>
          </button>

        </div>

        <!-- Named camera selector (desktop with 3+ cameras, or tablets) -->
        <div *ngIf="availableCameras.length > 2" class="mt-4 flex gap-2 justify-center flex-wrap">
          <button *ngFor="let cam of availableCameras; let i = index"
            (click)="selectCamera(cam.deviceId)"
            [disabled]="isSwitching"
            class="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border disabled:opacity-50"
            [class.bg-[#D31148]]="cam.deviceId === selectedCameraId"
            [class.border-[#D31148]]="cam.deviceId === selectedCameraId"
            [class.text-white]="cam.deviceId === selectedCameraId"
            [class.bg-transparent]="cam.deviceId !== selectedCameraId"
            [class.border-white/20]="cam.deviceId !== selectedCameraId"
            [class.text-gray-300]="cam.deviceId !== selectedCameraId">
            {{ cam.label || ('Camera ' + (i + 1)) }}
          </button>
        </div>

        <!-- Mirror hint for front camera -->
        <p *ngIf="streamActive && isFrontCamera && !capturedImage"
          class="mt-3 text-center text-[11px] text-gray-500 leading-snug">
          Preview is mirrored — your photo will save in real-world orientation
        </p>

      </ng-container>

      <!-- PREVIEW state -->
      <ng-container *ngIf="capturedImage">
        <div class="flex items-center gap-3">
          <button (click)="retakePhoto()"
            class="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-white/10 hover:bg-white/15 text-white text-sm font-semibold transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M1 4v6h6M23 20v-6h-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Retake
          </button>
          <button (click)="usePhoto()"
            class="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-[#D31148] hover:bg-[#b80e3d] text-white text-sm font-semibold transition-colors shadow-lg">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Use Photo
          </button>
        </div>
      </ng-container>

    </div>
  </div>
</div>